import { useState } from 'react'
import { cn } from '@/lib/utils'
import { If } from '@/utility_components/ControlStatements'
import {
  TESTIMONIAL_LIST_TRUNCATE_LENGTH,
  TESTIMONIAL_TRUNCATE_SUFFIX,
} from 'app/ProviderDetails/constants'
import { getTestimonialAttribution } from 'app/ProviderDetails/utils'

export type TestimonialCardProps = {
  item: ProviderTestimonialItem
  providerFirstName: string
  onReadMoreClick?: (itemId: string) => void
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  item,
  providerFirstName,
  onReadMoreClick,
}) => {
  const [expanded, setExpanded] = useState(false)
  const body = item.reviewBody?.trim() ?? ''
  const isLong = body.length > TESTIMONIAL_LIST_TRUNCATE_LENGTH
  const displayBody =
    expanded || !isLong
      ? body
      : body.slice(0, TESTIMONIAL_LIST_TRUNCATE_LENGTH) +
        TESTIMONIAL_TRUNCATE_SUFFIX
  const attribution = getTestimonialAttribution(item, providerFirstName)

  const handleReadMore = () => {
    if (onReadMoreClick) {
      onReadMoreClick(item.id)
    } else {
      setExpanded(!expanded)
    }
  }

  // In controlled mode the parent owns the expanded state, so local `expanded`
  // stays false and is meaningless — omit aria-expanded entirely so assistive
  // tech doesn't receive a permanently-collapsed signal.
  const ariaExpanded = onReadMoreClick ? undefined : expanded

  return (
    <div
      className={cn(
        '~border ~border-grey-wash ~rounded ~flex ~flex-col ~py-12 ~px-8 md:~py-8 md:~px-12',
        '~bg-light-grey/70'
      )}
    >
      <div className="~shrink-0 ~h-10 ~w-[43px] ~mb-6">
        <img
          src="/static/quote-icon.svg"
          alt=""
          className="~block ~h-10 ~w-auto ~max-w-[43px] ~object-contain ~object-left-top"
          width={43}
          height={40}
          aria-hidden
        />
      </div>
      <div className="~flex-1">
        <p className="~font-mrseavesxl ~text-2xl ~font-bold ~text-charcoal ~m-0">
          {displayBody}
        </p>
        <If condition={isLong}>
          <button
            type="button"
            className={cn(
              '~font-gibson ~text-base ~font-medium ~text-link ~bg-transparent ~border-0 ~p-0 ~mt-2 ~cursor-pointer ~text-left ~rounded-2xs ~no-underline',
              '~outline-none ~focus-visible:~outline-2 ~focus-visible:~outline-link ~focus-visible:~outline-offset-2'
            )}
            aria-expanded={ariaExpanded}
            onClick={handleReadMore}
          >
            {expanded ? 'Read less' : 'Read more'}
          </button>
        </If>
      </div>
      <p className="~font-gibson ~text-sm ~font-normal ~text-charcoal ~m-0 ~mt-2">
        {attribution}
      </p>
    </div>
  )
}

export default TestimonialCard
